import React, { useEffect, useMemo, useState } from "react";
import { api } from "../clinicApi";
import "./ClinicWeeklySchedule7Days.css";

import ScheduleModal from "../ScheduleModal";
import ConfirmModal from "../ConfirmModal";
import InfoModal from "../InfoModal";
import MobileFilterModal from "./MobileFilterModal";

/* ===== utils ===== */
const dowVN = (d) =>
  ["Chủ Nhật", "Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"][
    new Date(d).getDay()
  ];

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const toISO = (d) => {
  if (!d) return "";
  if (typeof d === "string") return d.slice(0, 10);
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(dt.getDate()).padStart(2, "0")}`;
};

const fmtDMY = (d) => {
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, "0")}/${String(
    dt.getMonth() + 1
  ).padStart(2, "0")}`;
};

export default function ClinicWeeklySchedule7Days() {
  /* ===== DATA ===== */
  const [rooms, setRooms] = useState([]);
  const [schedules, setSchedules] = useState([]);

  /* ===== FILTER STATE ===== */
  const [filterRoom, setFilterRoom] = useState("");
  const [filterDoctor, setFilterDoctor] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [keyword, setKeyword] = useState("");

  /* ===== MOBILE FILTER ===== */
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const resetFilter = () => {
    setFilterRoom("");
    setFilterDoctor("");
    setFilterDate("");
    setKeyword("");
  };

  /* ===== MODALS ===== */
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSchedule, setModalSchedule] = useState(null);

  const [confirm, setConfirm] = useState({
    open: false,
    title: "",
    message: "",
    onOk: null,
  });

  const [info, setInfo] = useState({
    open: false,
    title: "",
    message: "",
  });

  /* ===== 7 DAYS ===== */
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(today, i)),
    [today]
  );

  /* ===== LOAD DATA ===== */
  useEffect(() => {
    refreshAll();
  }, []);

  const refreshAll = async () => {
    const res = await api.schedules.list();

    const formatted = res.map((x) => ({
      ...x,
      ngayISO: toISO(x.ngay),
      caText: x.buoi === 0 ? "Sáng" : "Chiều",
    }));

    const phongMap = new Map();
    formatted.forEach((x) => {
      if (x.tenPhongKham) {
        phongMap.set(x.id_PhongKham, {
          id: x.id_PhongKham,
          tenPhong: x.tenPhongKham,
        });
      }
    });

    setRooms([...phongMap.values()]);
    setSchedules(formatted);
  };

  /* ===== FILTER LOGIC ===== */
  const filteredSchedules = useMemo(() => {
    return schedules.filter((s) => {
      if (filterRoom && String(s.id_PhongKham) !== filterRoom) return false;
      if (filterDoctor && s.tenBS !== filterDoctor) return false;
      if (filterDate && s.ngayISO !== filterDate) return false;

      if (keyword) {
        const kw = keyword.toLowerCase();
        if (
          !(
            (s.tenBS || "").toLowerCase().includes(kw) ||
            (s.tenPhongKham || "").toLowerCase().includes(kw)
          )
        ) {
          return false;
        }
      }
      return true;
    });
  }, [schedules, filterRoom, filterDoctor, filterDate, keyword]);

  /* ===== MAP DATA ===== */
  const dataMap = useMemo(() => {
    const map = {};
    rooms.forEach((r) => (map[r.id] = {}));

    filteredSchedules.forEach((s) => {
      if (!map[s.id_PhongKham]) return;
      if (!map[s.id_PhongKham][s.ngayISO]) {
        map[s.id_PhongKham][s.ngayISO] = [];
      }
      map[s.id_PhongKham][s.ngayISO].push(s);
    });

    return map;
  }, [rooms, filteredSchedules]);

  const pickByCa = (list, ca) => list.filter((s) => s.caText === ca);

  /* ===== CELL ===== */
  const renderCell = (arr) => {
    if (!arr.length) return <td />;

    return (
      <td className="cell">
        {arr.map((s, i) => (
          <div
            key={i}
            className="doc-name"
            onClick={() => {
              setModalSchedule(s);
              setModalOpen(true);
            }}
          >
            {s.tenBS}
          </div>
        ))}
      </td>
    );
  };

  /* ===== OPTIONS ===== */
  const doctorOptions = [...new Set(schedules.map((s) => s.tenBS))].filter(
    Boolean
  );

  return (
    <div className="week7-container">
      {/* ===== FILTER BAR (DESKTOP) ===== */}
      <div className="week7-filter">
        <div className="filter-left">
          <img
            src="https://image.bvhvgl.com/images/LoGo%20HVGL/logo_chinh.png"
            alt="logo"
            className="filter-icon"
          />
        </div>

        <div className="filter-right">
          <select
            value={filterRoom}
            onChange={(e) => setFilterRoom(e.target.value)}
          >
            <option value="">Tất cả phòng</option>
            {rooms.map((r) => (
              <option key={r.id} value={r.id}>
                {r.tenPhong}
              </option>
            ))}
          </select>

          <select
            value={filterDoctor}
            onChange={(e) => setFilterDoctor(e.target.value)}
          >
            <option value="">Tất cả bác sĩ</option>
            {doctorOptions.map((d, i) => (
              <option key={i} value={d}>
                {d}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
          />

          <input
            type="text"
            placeholder="Tìm kiếm..."
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </div>
      </div>

      {/* ===== TABLE ===== */}
      <div className="table-wrap">
        <table className="week7-table">
          <thead>
            <tr>
              <th rowSpan={2} className="left-sticky">
                Phòng khám
              </th>
              {weekDays.map((d, i) => (
                <th key={i} colSpan={2}>
                  <div>{dowVN(d)}</div>
                  <div>{fmtDMY(d)}</div>
                </th>
              ))}
            </tr>
            <tr>
              {weekDays.map((_, i) => (
                <React.Fragment key={i}>
                  <th>Sáng</th>
                  <th>Chiều</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>

          <tbody>
            {rooms.map((r) => (
              <tr key={r.id}>
                <td className="room left-sticky">{r.tenPhong}</td>
                {weekDays.map((d, i) => {
                  const list = dataMap[r.id]?.[toISO(d)] || [];
                  return (
                    <React.Fragment key={i}>
                      {renderCell(pickByCa(list, "Sáng"))}
                      {renderCell(pickByCa(list, "Chiều"))}
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ===== MODALS ===== */}
      <ScheduleModal
        open={modalOpen}
        schedule={modalSchedule}
        onClose={() => setModalOpen(false)}
        readonly
      />

      <ConfirmModal {...confirm} />
      <InfoModal {...info} />

      {/* ===== MOBILE FILTER MODAL ===== */}
      <MobileFilterModal
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        rooms={rooms}
        doctors={doctorOptions}
        filterRoom={filterRoom}
        setFilterRoom={setFilterRoom}
        filterDoctor={filterDoctor}
        setFilterDoctor={setFilterDoctor}
        filterDate={filterDate}
        setFilterDate={setFilterDate}
        keyword={keyword}
        setKeyword={setKeyword}
        onReset={resetFilter}
      />

      {/* ===== FOOTER ===== */}
      <div className="week7-footer">
        © {new Date().getFullYear()} Hệ thống Y tế Hùng Vương Gia Lai® –
        leanhquan™
      </div>
      {/* ===== MOBILE FILTER FAB ===== */}
      <button
        className="mobile-filter-fab"
        onClick={() => setMobileFilterOpen(true)}
        aria-label="Mở bộ lọc"
      >
        ☰
      </button>
    </div>
  );
}
